import React, { useState } from 'react';
import axios from './api';
import { Search, Users, Eye, Share2, Heart, MessageCircle, TrendingUp, AlertCircle, CheckCircle, X, Shield, Brain, Loader2, BarChart3, Clock, Calendar, Activity, Zap, Target, TrendingDown, ArrowUp, ArrowDown, LineChart } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import './index.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

function App() {
  const [channelId, setChannelId] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [claudeLoading, setClaudeLoading] = useState(false);
  const [claudeAnalysis, setClaudeAnalysis] = useState(null);
  const [viewsAnalysis, setViewsAnalysis] = useState(null);
  const [viewsLoading, setViewsLoading] = useState(false);
  const [chartRef, setChartRef] = useState(null);

  const requestVerificationCode = async () => {
    setAuthLoading(true);
    try {
      const response = await axios.post('/api/auth/request-code');
      if (response.data.success) {
        if (response.data.code_required) {
          setShowAuthModal(true);
        } else if (response.data.authorized) {
          setIsAuthenticated(true);
        }
      } else {
        setError(response.data.error || 'Failed to request verification code');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to request verification code');
    } finally {
      setAuthLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter verification code');
      return;
    }

    setAuthLoading(true);
    try {
      const response = await axios.post('/api/auth/verify-code', {
        code: verificationCode.trim()
      });

      if (response.data.success && response.data.authorized) {
        setIsAuthenticated(true);
        setShowAuthModal(false);
        setVerificationCode('');
        setError('');
      } else {
        setError(response.data.error || 'Invalid verification code');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid verification code');
    } finally {
      setAuthLoading(false);
    }
  };

  const analyzeWithClaude = async () => {
    if (!channelId.trim()) {
      setError('Please enter a channel ID, username, or link');
      return;
    }

    // Check if we need authentication
    if (!isAuthenticated) {
      await requestVerificationCode();
      return;
    }

    setClaudeLoading(true);
    setError('');
    setClaudeAnalysis(null);

    try {
      const response = await axios.post('/api/analyze-with-claude', {
        channel_id: channelId.trim()
      });

      if (response.data.error) {
        if (response.data.error.includes('Authorization required')) {
          setIsAuthenticated(false);
          await requestVerificationCode();
        } else {
          setError(response.data.error);
        }
      } else {
        setClaudeAnalysis(response.data);
        // Also set the basic analysis for comparison
        setAnalysis(response.data.channel_data);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to analyze with Claude';
      if (errorMsg.includes('Authorization required')) {
        setIsAuthenticated(false);
        await requestVerificationCode();
      } else {
        setError(errorMsg);
      }
    } finally {
      setClaudeLoading(false);
    }
  };

  const getViewsPerMinute = async () => {
    if (!channelId.trim()) {
      setError('Please enter a channel ID, username, or link');
      return;
    }

    // Check if we need authentication
    if (!isAuthenticated) {
      await requestVerificationCode();
      return;
    }

    setViewsLoading(true);
    setError('');
    setViewsAnalysis(null);

    try {
      const response = await axios.post('/api/views-per-minute', {
        channel_id: channelId.trim()
      });

      if (response.data.error) {
        if (response.data.error.includes('Authorization required')) {
          setIsAuthenticated(false);
          await requestVerificationCode();
        } else {
          setError(response.data.error);
        }
      } else {
        setViewsAnalysis(response.data);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to get views analysis';
      if (errorMsg.includes('Authorization required')) {
        setIsAuthenticated(false);
        await requestVerificationCode();
      } else {
        setError(errorMsg);
      }
    } finally {
      setViewsLoading(false);
    }
  };

  const resetZoom = () => {
    if (chartRef) {
      chartRef.resetZoom();
    }
  };

  const analyzeChannel = async () => {
    if (!channelId.trim()) {
      setError('Please enter a channel ID, username, or link');
      return;
    }

    // Check if we need authentication
    if (!isAuthenticated) {
      await requestVerificationCode();
      return;
    }

    setLoading(true);
    setError('');
    setAnalysis(null);

    try {
      const response = await axios.post('/api/analyze-channel', {
        channel_id: channelId.trim()
      });

      if (response.data.error) {
        if (response.data.error.includes('Authorization required')) {
          setIsAuthenticated(false);
          await requestVerificationCode();
        } else {
          setError(response.data.error);
        }
      } else {
        setAnalysis(response.data);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to analyze channel';
      if (errorMsg.includes('Authorization required')) {
        setIsAuthenticated(false);
        await requestVerificationCode();
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      analyzeChannel();
    }
  };

  const handleAuthKeyPress = (e) => {
    if (e.key === 'Enter') {
      verifyCode();
    }
  };

  return (
    <div className="min-h-screen p-2 sm:p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 pt-6 px-2">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">Telegram Channel Analyzer</h1>
          <p className="text-white/80 text-sm sm:text-base">Analyze Telegram channels with Telethon</p>
          {isAuthenticated && (
            <div className="mt-2 inline-flex items-center gap-2 bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-xs sm:text-sm">
              <Shield size={16} />
              Authenticated
            </div>
          )}
        </div>

        {/* Authentication Modal */}
        {showAuthModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg sm:text-xl font-bold flex items-center gap-2">
                  <Shield className="text-purple-600" size={24} />
                  <span className="hidden sm:inline">Telegram Authentication</span>
                  <span className="sm:hidden">Auth</span>
                </h3>
                <button
                  onClick={() => setShowAuthModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-gray-600 mb-2">
                  A verification code has been sent to your Telegram account. 
                  Please enter the code below to authenticate.
                </p>
                <p className="text-sm text-gray-500">
                  Check your Telegram app for the code from Telegram.
                </p>
              </div>

              <div className="space-y-4">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  onKeyPress={handleAuthKeyPress}
                  placeholder="Enter verification code"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoFocus
                />
                
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowAuthModal(false)}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={verifyCode}
                    disabled={authLoading}
                    className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {authLoading ? 'Verifying...' : 'Verify Code'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Input Section */}
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-6">
          {/* Search Input */}
          <div className="mb-4">
            <input
              type="text"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter channel ID, username, or link"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base"
            />
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              onClick={analyzeChannel}
              disabled={loading}
              className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors text-sm sm:text-base min-h-[44px]"
            >
              <Search size={18} />
              <span>{loading ? 'Analyzing...' : 'Analyze Channel'}</span>
            </button>
            <button
              onClick={analyzeWithClaude}
              disabled={claudeLoading}
              className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all text-sm sm:text-base min-h-[44px]"
            >
              <Brain size={18} />
              <span>{claudeLoading ? 'AI Analyzing...' : 'Claude AI'}</span>
            </button>
            <button
              onClick={getViewsPerMinute}
              disabled={viewsLoading}
              className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg hover:from-green-700 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all text-sm sm:text-base min-h-[44px]"
            >
              <LineChart size={18} />
              <span>{viewsLoading ? 'Analyzing...' : 'Views Per Minute'}</span>
            </button>
          </div>

          {error && (
            <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="text-red-500" size={window.innerWidth >= 640 ? 20 : 16} />
              <span className="text-red-700 text-xs sm:text-sm">{error}</span>
            </div>
          )}

          <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2 text-sm sm:text-base">Accepted Formats:</h3>
            <div className="text-xs sm:text-sm text-blue-800 space-y-1">
              <div>• <code className="bg-blue-100 px-1 rounded">@channelname</code> - Channel username</div>
              <div>• <code className="bg-blue-100 px-1 rounded">-1001234567890</code> - Numeric channel ID</div>
              <div>• <code className="bg-blue-100 px-1 rounded">https://t.me/channelname</code> - Telegram link</div>
              <div className="hidden sm:block">• <code className="bg-blue-100 px-1 rounded">https://t.me/joinchat/ABCDEF</code> - Invite link</div>
              <div className="hidden sm:block">• <code className="bg-blue-100 px-1 rounded">channelname</code> - Username without @</div>
            </div>
          </div>
        </div>

        {/* Views Loading State */}
        {viewsLoading && (
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-12 text-center">
            <div className="flex flex-col items-center gap-3 sm:gap-4">
              <Loader2 className="animate-spin text-green-600" size={window.innerWidth >= 640 ? 48 : 32} />
              <LineChart className="text-teal-600" size={window.innerWidth >= 640 ? 32 : 24} />
              <p className="text-gray-600 text-sm sm:text-lg">Analyzing views per minute...</p>
              <p className="text-gray-500 text-xs sm:text-sm">This may take a moment as we gather view data</p>
            </div>
          </div>
        )}

        {/* Claude Loading State */}
        {claudeLoading && (
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-12 text-center">
            <div className="flex flex-col items-center gap-3 sm:gap-4">
              <Loader2 className="animate-spin text-purple-600" size={window.innerWidth >= 640 ? 48 : 32} />
              <Brain className="text-blue-600" size={window.innerWidth >= 640 ? 32 : 24} />
              <p className="text-gray-600 text-sm sm:text-lg">Claude AI is analyzing the channel...</p>
              <p className="text-gray-500 text-xs sm:text-sm">This may take a moment as we gather comprehensive insights</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && !claudeLoading && (
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-12 text-center">
            <div className="loading-spinner mx-auto mb-3 sm:mb-4 w-8 h-8 sm:w-10 sm:h-10"></div>
            <p className="text-gray-600 text-sm sm:text-base">Analyzing channel...</p>
          </div>
        )}

        {/* Claude Analysis Results */}
        {claudeAnalysis && (
          <div className="space-y-6">
            {/* Claude AI Analysis */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl shadow-xl p-4 sm:p-6 border border-purple-200">
              <h2 className="text-lg sm:text-2xl font-bold mb-4 flex items-center gap-2">
                <Brain className="text-purple-600" size={window.innerWidth >= 640 ? 28 : 20} />
                <span className="hidden sm:inline">Claude AI Analysis</span>
                <span className="sm:hidden">AI Analysis</span>
              </h2>
              
              {claudeAnalysis.claude_analysis?.error ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700">{claudeAnalysis.claude_analysis.error}</p>
                </div>
              ) : (
                <div className="prose max-w-none">
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">
                      {claudeAnalysis.claude_analysis?.analysis || 'No analysis available'}
                    </pre>
                  </div>
                  {claudeAnalysis.claude_analysis?.model_used && (
                    <p className="text-xs text-gray-500 italic">
                      Analysis powered by {claudeAnalysis.claude_analysis.model_used}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Sentiment Analysis */}
            {claudeAnalysis.sentiment_analysis && !claudeAnalysis.sentiment_analysis.error && (
              <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold mb-4 flex items-center gap-2">
                  <Heart className="text-red-500" size={window.innerWidth >= 640 ? 24 : 20} />
                  <span className="hidden sm:inline">Message Sentiment Analysis</span>
                  <span className="sm:hidden">Sentiment</span>
                </h3>
                <div className="prose max-w-none">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">
                      {claudeAnalysis.sentiment_analysis.sentiment_analysis}
                    </pre>
                  </div>
                  {claudeAnalysis.sentiment_analysis.messages_analyzed && (
                    <p className="text-xs text-gray-500 mt-2">
                      Based on {claudeAnalysis.sentiment_analysis.messages_analyzed} recent messages
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Views Per Minute Analysis Results */}
        {viewsAnalysis && (
          <div className="space-y-6">
            {/* Recent Post Info */}
            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-2xl font-bold mb-4 flex items-center gap-2">
                <LineChart className="text-green-600" size={window.innerWidth >= 640 ? 28 : 20} />
                <span className="hidden sm:inline">Views Per Minute Analysis</span>
                <span className="sm:hidden">Views Analysis</span>
              </h2>
              
              {/* Recent Post Details */}
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4">
                <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">Recent Post Analyzed</h3>
                <p className="text-gray-600 text-xs sm:text-sm mb-2">
                  {viewsAnalysis.recent_post?.text || 'Media or non-text message'}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                  <span>Message ID: {viewsAnalysis.recent_post?.message_id}</span>
                  <span className="hidden sm:inline">•</span>
                  <span>Posted: {viewsAnalysis.recent_post?.date ? new Date(viewsAnalysis.recent_post.date).toLocaleString() : 'N/A'}</span>
                </div>
              </div>

              {/* Key Statistics */}
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Total Views</p>
                  <p className="text-lg sm:text-2xl font-bold text-blue-600">
                    {viewsAnalysis.analysis?.total_views?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Avg Views/Minute</p>
                  <p className="text-lg sm:text-2xl font-bold text-green-600">
                    {Math.round(viewsAnalysis.analysis?.average_views_per_minute || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Peak Views/Minute</p>
                  <p className="text-lg sm:text-2xl font-bold text-purple-600">
                    {viewsAnalysis.analysis?.peak_views_per_minute?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 sm:p-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Engagement Level</p>
                  <p className="text-base sm:text-lg font-bold capitalize text-orange-600">
                    {viewsAnalysis.analysis?.engagement_level?.replace('_', ' ') || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Chart */}
              {viewsAnalysis.graph_data && (
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                    <h3 className="text-base sm:text-lg font-semibold">New Views Per Minute Analysis</h3>
                    <button
                      onClick={resetZoom}
                      className="px-2 sm:px-3 py-1 bg-blue-500 text-white text-xs sm:text-sm rounded hover:bg-blue-600 transition-colors"
                    >
                      Reset Zoom
                    </button>
                  </div>
                  <div className="mb-2 text-xs sm:text-sm text-gray-600">
                    💡 <strong>Tip:</strong> <span className="hidden sm:inline">Use mouse wheel to zoom, click and drag to pan. </span>Shows new views gained every minute!
                  </div>
                  <div className="h-64">
                    <Line
                      ref={setChartRef}
                      data={{
                        labels: viewsAnalysis.graph_data.labels,
                        datasets: [
                          {
                            label: 'New Views per Minute',
                            data: viewsAnalysis.graph_data.new_views,
                            borderColor: 'rgb(16, 185, 129)',
                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                            fill: true,
                            tension: 0.4,
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: {
                          mode: 'index',
                          intersect: false,
                        },
                        plugins: {
                          title: {
                            display: false
                          },
                          legend: {
                            position: 'top',
                          },
                          zoom: {
                            zoom: {
                              wheel: {
                                enabled: true,
                                speed: 0.1
                              },
                              pinch: {
                                enabled: true
                              },
                              mode: 'x',
                            },
                            pan: {
                              enabled: true,
                              mode: 'x',
                            }
                          },
                          tooltip: {
                            callbacks: {
                              title: function(context) {
                                return `Time: ${context[0].label}`;
                              },
                              label: function(context) {
                                return `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`;
                              }
                            }
                          }
                        },
                        scales: {
                          x: {
                            display: true,
                            title: {
                              display: true,
                              text: 'Time (minutes)'
                            },
                            ticks: {
                              maxTicksLimit: 20,
                              callback: function(value, index, values) {
                                // Show every 5th tick to avoid overcrowding
                                if (index % 5 === 0 || index === values.length - 1) {
                                  return this.getLabelForValue(value);
                                }
                                return '';
                              }
                            }
                          },
                          y: {
                            type: 'linear',
                            display: true,
                            position: 'left',
                            title: {
                              display: true,
                              text: 'New Views per Minute'
                            },
                            ticks: {
                              callback: function(value) {
                                return value.toLocaleString();
                              }
                            }
                          },
                        }
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Additional Analysis */}
              <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4 mt-4">
                <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Message Age</p>
                  <p className="text-base sm:text-lg font-bold text-blue-600">
                    {viewsAnalysis.analysis?.message_age_minutes || 0} minutes
                  </p>
                </div>
                <div className="bg-teal-50 rounded-lg p-3 sm:p-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Views Growth Rate</p>
                  <p className="text-base sm:text-lg font-bold text-teal-600">
                    {viewsAnalysis.analysis?.views_growth_rate || 0} views/min
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analysis Results */}
        {analysis && (
          <div className="space-y-6">
            {/* Channel Info */}
            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-2xl font-bold mb-4 flex items-center gap-2">
                <Users className="text-purple-600" size={window.innerWidth >= 640 ? 28 : 20} />
                <span className="hidden sm:inline">Channel Information</span>
                <span className="sm:hidden">Channel Info</span>
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div>
                  <p className="text-gray-600 text-xs sm:text-sm">Channel Name</p>
                  <p className="font-semibold text-base sm:text-lg">{analysis.basic_info?.title || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs sm:text-sm">Username</p>
                  <p className="font-semibold text-base sm:text-lg">@{analysis.basic_info?.username || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs sm:text-sm">Members</p>
                  <p className="font-semibold text-base sm:text-lg">{analysis.basic_info?.member_count?.toLocaleString() || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-600 text-xs sm:text-sm">Type</p>
                  <p className="font-semibold text-base sm:text-lg capitalize">{analysis.basic_info?.type || 'N/A'}</p>
                </div>
              </div>

              {analysis.basic_info?.description && (
                <div className="mt-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Description</p>
                  <p className="font-semibold text-sm sm:text-base">{analysis.basic_info.description}</p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-4">
                {analysis.basic_info?.is_verified && (
                  <div className="flex items-center gap-1 text-blue-600">
                    <CheckCircle size={window.innerWidth >= 640 ? 20 : 16} />
                    <span className="font-medium text-sm sm:text-base">Verified</span>
                  </div>
                )}
                {analysis.basic_info?.invite_link && (
                  <a
                    href={analysis.basic_info.invite_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-700 font-medium text-sm sm:text-base"
                  >
                    View Invite Link
                  </a>
                )}
              </div>
            </div>

            {/* Data Completeness */}
            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-2xl font-bold mb-4 flex items-center gap-2">
                <BarChart3 className="text-purple-600" size={window.innerWidth >= 640 ? 28 : 20} />
                <span className="hidden sm:inline">Data Completeness</span>
                <span className="sm:hidden">Data</span>
              </h2>
              
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Messages Analyzed</p>
                  <p className="text-lg sm:text-2xl font-bold text-blue-600">
                    {analysis.data_completeness?.total_messages_fetched?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Days Covered</p>
                  <p className="text-lg sm:text-2xl font-bold text-green-600">
                    {analysis.data_completeness?.days_covered || 0}
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Data Quality</p>
                  <p className="text-base sm:text-lg font-bold capitalize text-purple-600">
                    {analysis.data_completeness?.data_quality || 'N/A'}
                  </p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 sm:p-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Date Range</p>
                  <p className="text-xs sm:text-sm font-semibold text-orange-600">
                    {analysis.data_completeness?.date_range_start || 'N/A'} to {analysis.data_completeness?.date_range_end || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Key Statistics */}
            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
              <h2 className="text-lg sm:text-2xl font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="text-purple-600" size={window.innerWidth >= 640 ? 28 : 20} />
                <span className="hidden sm:inline">Key Statistics</span>
                <span className="sm:hidden">Stats</span>
              </h2>
              
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="text-purple-600" size={window.innerWidth >= 640 ? 20 : 16} />
                    <p className="text-gray-600 text-xs sm:text-sm">Total Messages</p>
                  </div>
                  <p className="text-lg sm:text-2xl font-bold text-purple-600">
                    {analysis.statistics?.total_messages_analyzed?.toLocaleString() || 0}
                  </p>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="text-blue-600" size={window.innerWidth >= 640 ? 20 : 16} />
                    <p className="text-gray-600 text-xs sm:text-sm">Avg Views</p>
                  </div>
                  <p className="text-lg sm:text-2xl font-bold text-blue-600">
                    {Math.round(analysis.statistics?.average_views_per_message || 0).toLocaleString()}
                  </p>
                </div>
                
                <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Share2 className="text-green-600" size={window.innerWidth >= 640 ? 20 : 16} />
                    <p className="text-gray-600 text-xs sm:text-sm">Total Forwards</p>
                  </div>
                  <p className="text-lg sm:text-2xl font-bold text-green-600">
                    {analysis.statistics?.total_forwards?.toLocaleString() || 0}
                  </p>
                </div>
                
                <div className="bg-red-50 rounded-lg p-3 sm:p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="text-red-600" size={window.innerWidth >= 640 ? 20 : 16} />
                    <p className="text-gray-600 text-xs sm:text-sm">Engagement Rate</p>
                  </div>
                  <p className="text-lg sm:text-2xl font-bold text-red-600">
                    {analysis.statistics?.engagement_rate || 0}%
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-4">
                <div className="bg-indigo-50 rounded-lg p-3 sm:p-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Media Content</p>
                  <p className="text-base sm:text-xl font-bold text-indigo-600">
                    {analysis.statistics?.media_content_percentage || 0}%
                  </p>
                </div>
                <div className="bg-teal-50 rounded-lg p-3 sm:p-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Forward Rate</p>
                  <p className="text-base sm:text-xl font-bold text-teal-600">
                    {analysis.statistics?.forward_rate || 0}%
                  </p>
                </div>
                <div className="bg-pink-50 rounded-lg p-3 sm:p-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Total Reactions</p>
                  <p className="text-base sm:text-xl font-bold text-pink-600">
                    {analysis.statistics?.total_reactions?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3 sm:p-4">
                  <p className="text-gray-600 text-xs sm:text-sm">Reaction Rate</p>
                  <p className="text-base sm:text-xl font-bold text-yellow-600">
                    {analysis.statistics?.reaction_rate || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Engagement Analysis */}
            {analysis.engagement_analysis && (
              <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-2xl font-bold mb-4 flex items-center gap-2">
                  <Activity className="text-purple-600" size={window.innerWidth >= 640 ? 28 : 20} />
                  <span className="hidden sm:inline">Engagement Analysis</span>
                  <span className="sm:hidden">Engagement</span>
                </h2>
                
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Avg Reactions/Message</p>
                    <p className="text-base sm:text-xl font-bold text-blue-600">
                      {analysis.engagement_analysis?.average_reactions_per_message || 0}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Avg Forwards/Message</p>
                    <p className="text-base sm:text-xl font-bold text-green-600">
                      {analysis.engagement_analysis?.average_forwards_per_message || 0}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">High Engagement Messages</p>
                    <p className="text-base sm:text-xl font-bold text-purple-600">
                      {analysis.engagement_analysis?.messages_with_high_engagement || 0}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">High Engagement %</p>
                    <p className="text-base sm:text-xl font-bold text-orange-600">
                      {analysis.engagement_analysis?.view_statistics?.high_engagement_percentage || 0}%
                    </p>
                  </div>
                </div>

                {analysis.engagement_analysis?.view_statistics && (
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-gray-600 text-xs sm:text-sm">Max Views</p>
                      <p className="text-base sm:text-lg font-bold text-gray-800">
                        {analysis.engagement_analysis.view_statistics.max_views?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-gray-600 text-xs sm:text-sm">Min Views</p>
                      <p className="text-base sm:text-lg font-bold text-gray-800">
                        {analysis.engagement_analysis.view_statistics.min_views?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-gray-600 text-xs sm:text-sm">Median Views</p>
                      <p className="text-base sm:text-lg font-bold text-gray-800">
                        {Math.round(analysis.engagement_analysis.view_statistics.median_views || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Content Analysis */}
            {analysis.content_analysis && (
              <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-2xl font-bold mb-4 flex items-center gap-2">
                  <Target className="text-purple-600" size={window.innerWidth >= 640 ? 28 : 20} />
                  <span className="hidden sm:inline">Content Analysis</span>
                  <span className="sm:hidden">Content</span>
                </h2>
                
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Avg Message Length</p>
                    <p className="text-base sm:text-xl font-bold text-blue-600">
                      {Math.round(analysis.content_analysis?.average_message_length || 0)} chars
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Reply Percentage</p>
                    <p className="text-base sm:text-xl font-bold text-green-600">
                      {analysis.content_analysis?.reply_percentage || 0}%
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Forward Percentage</p>
                    <p className="text-base sm:text-xl font-bold text-purple-600">
                      {analysis.content_analysis?.forward_percentage || 0}%
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Total Replies</p>
                    <p className="text-base sm:text-xl font-bold text-orange-600">
                      {analysis.content_analysis?.total_replies || 0}
                    </p>
                  </div>
                </div>

                {analysis.content_analysis?.media_type_distribution && (
                  <div className="mt-6">
                    <h3 className="text-base sm:text-lg font-semibold mb-3">Media Type Distribution</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(analysis.content_analysis.media_type_distribution).map(([type, count]) => (
                        <div key={type} className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-xs sm:text-sm text-gray-600 capitalize">{type}</p>
                          <p className="text-base sm:text-lg font-bold text-gray-800">{count?.toLocaleString() || 0}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Time Analysis */}
            {analysis.time_analysis && (
              <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                  <h2 className="text-lg sm:text-2xl font-bold flex items-center gap-2">
                    <Clock className="text-purple-600" size={window.innerWidth >= 640 ? 28 : 20} />
                    <span className="hidden sm:inline">Time Analysis</span>
                    <span className="sm:hidden">Time</span>
                  </h2>
                  <div className="bg-blue-100 text-blue-800 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                    <span className="hidden sm:inline">Last 30 Days </span>
                    <span className="sm:hidden">30d </span>
                    ({analysis.time_analysis.messages_analyzed || 0} messages)
                  </div>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Most Active Day</p>
                    <p className="text-base sm:text-xl font-bold text-blue-600 capitalize">
                      {analysis.time_analysis?.most_active_day || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Most Active Hour</p>
                    <p className="text-base sm:text-xl font-bold text-green-600">
                      {analysis.time_analysis?.most_active_hour || 0}:00
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-gray-600 text-xs sm:text-sm">7-Day Trend</p>
                      {analysis.time_analysis?.activity_trend_7_days > 0 ? (
                        <ArrowUp className="text-green-600" size={window.innerWidth >= 640 ? 16 : 12} />
                      ) : (
                        <ArrowDown className="text-red-600" size={window.innerWidth >= 640 ? 16 : 12} />
                      )}
                    </div>
                    <p className="text-base sm:text-xl font-bold text-purple-600">
                      {analysis.time_analysis?.activity_trend_7_days || 0}%
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Recent Activity</p>
                    <p className="text-base sm:text-xl font-bold text-orange-600">
                      {analysis.time_analysis?.recent_activity_count || 0} messages
                    </p>
                  </div>
                </div>

                {analysis.time_analysis?.daily_distribution && (
                  <div className="mt-6">
                    <h3 className="text-base sm:text-lg font-semibold mb-3">Daily Distribution (Avg Views)</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3">
                      {Object.entries(analysis.time_analysis.daily_distribution).map(([day, avg_views]) => (
                        <div key={day} className="bg-gray-50 rounded-lg p-2 sm:p-3 text-center">
                          <p className="text-xs text-gray-600">{day.slice(0, 3)}</p>
                          <p className="text-sm sm:text-lg font-bold text-gray-800">{Math.round(avg_views || 0).toLocaleString()}</p>
                          <p className="text-xs text-gray-500 hidden sm:block">avg views</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 text-xs sm:text-sm text-gray-600">
                      Best Day: <span className="font-semibold">{analysis.time_analysis.best_day_by_avg_views || 'N/A'}</span> ({Math.round(analysis.time_analysis.best_day_avg_views || 0).toLocaleString()} avg views)
                    </div>
                  </div>
                )}

                {analysis.time_analysis?.hourly_distribution && (
                  <div className="mt-6">
                    <h3 className="text-base sm:text-lg font-semibold mb-3">Hourly Distribution (24h) - Avg Views</h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1 sm:gap-2">
                      {Object.entries(analysis.time_analysis.hourly_distribution).map(([hour, avg_views]) => (
                        <div key={hour} className="bg-gray-50 rounded-lg p-1 sm:p-2 text-center">
                          <p className="text-xs text-gray-600">{hour}:00</p>
                          <p className="text-xs sm:text-sm font-bold text-gray-800">{Math.round(avg_views || 0).toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 text-xs sm:text-sm text-gray-600">
                      Best Hour: <span className="font-semibold">{analysis.time_analysis.best_hour_by_avg_views || 'N/A'}:00</span> ({Math.round(analysis.time_analysis.best_hour_avg_views || 0).toLocaleString()} avg views)
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Member Analysis */}
            {analysis.member_analysis && (
              <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-2xl font-bold mb-4 flex items-center gap-2">
                  <Users className="text-purple-600" size={window.innerWidth >= 640 ? 28 : 20} />
                  <span className="hidden sm:inline">Member Analysis</span>
                  <span className="sm:hidden">Members</span>
                </h2>
                
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Total Members Analyzed</p>
                    <p className="text-lg sm:text-2xl font-bold text-blue-600">
                      {analysis.member_analysis?.total_members_analyzed?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Bot Count</p>
                    <p className="text-lg sm:text-2xl font-bold text-green-600">
                      {analysis.member_analysis?.bot_count || 0}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Premium Users</p>
                    <p className="text-lg sm:text-2xl font-bold text-purple-600">
                      {analysis.member_analysis?.premium_count || 0}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">With Username</p>
                    <p className="text-lg sm:text-2xl font-bold text-orange-600">
                      {analysis.member_analysis?.with_username_count || 0}
                    </p>
                  </div>
                </div>

                {analysis.member_analysis?.coverage_percentage && (
                  <div className="mt-4 bg-indigo-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Coverage of Total Members</p>
                    <p className="text-base sm:text-xl font-bold text-indigo-600">
                      {analysis.member_analysis.coverage_percentage}% 
                      {analysis.member_analysis.current_member_count && 
                        `(${analysis.member_analysis.unique_member_ids?.toLocaleString() || analysis.member_analysis.total_members_analyzed?.toLocaleString() || 0} / ${analysis.member_analysis.current_member_count?.toLocaleString() || 0})`
                      }
                    </p>
                  </div>
                )}

                {analysis.member_analysis?.member_status_distribution && (
                  <div className="mt-6">
                    <h3 className="text-base sm:text-lg font-semibold mb-3">Member Status Distribution</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(analysis.member_analysis.member_status_distribution).map(([status, count]) => (
                        <div key={status} className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-xs sm:text-sm text-gray-600 capitalize">{status}</p>
                          <p className="text-base sm:text-lg font-bold text-gray-800">{count?.toLocaleString() || 0}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {analysis.member_analysis?.recent_joins && (
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-gray-600 text-xs sm:text-sm">Recent Joins Count</p>
                      <p className="text-base sm:text-lg font-bold text-gray-800">
                        {analysis.member_analysis.recent_joins.recent_joins_count || 0}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                      <p className="text-gray-600 text-xs sm:text-sm">Current Member Count</p>
                      <p className="text-base sm:text-lg font-bold text-gray-800">
                        {analysis.member_analysis?.current_member_count?.toLocaleString() || 'N/A'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Growth Analysis */}
            {analysis.growth_analysis && (
              <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-2xl font-bold mb-4 flex items-center gap-2">
                  {analysis.growth_analysis?.growth_indicator === 'declining' ? (
                    <TrendingDown className="text-red-600" size={window.innerWidth >= 640 ? 28 : 20} />
                  ) : (
                    <TrendingUp className="text-green-600" size={window.innerWidth >= 640 ? 28 : 20} />
                  )}
                  <span className="hidden sm:inline">Growth Analysis</span>
                  <span className="sm:hidden">Growth</span>
                </h2>
                
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                  <div className={`rounded-lg p-3 sm:p-4 ${analysis.growth_analysis?.growth_indicator === 'declining' ? 'bg-red-50' : 'bg-green-50'}`}>
                    <p className="text-gray-600 text-xs sm:text-sm">Growth Indicator</p>
                    <p className={`text-base sm:text-xl font-bold capitalize ${analysis.growth_analysis?.growth_indicator === 'declining' ? 'text-red-600' : 'text-green-600'}`}>
                      {analysis.growth_analysis?.growth_indicator || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Posting Frequency Growth</p>
                    <p className="text-base sm:text-xl font-bold text-blue-600">
                      {analysis.growth_analysis?.posting_frequency_growth || 0}%
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">View Engagement Growth</p>
                    <p className="text-base sm:text-xl font-bold text-purple-600">
                      {analysis.growth_analysis?.view_engagement_growth || 0}%
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Analysis Period</p>
                    <p className="text-base sm:text-xl font-bold text-orange-600">
                      {analysis.growth_analysis?.analysis_period_days || 0} days
                    </p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Older Daily Avg Posts</p>
                    <p className="text-base sm:text-lg font-bold text-gray-800">
                      {Math.round(analysis.growth_analysis?.older_daily_average_posts || 0)}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-600 text-xs sm:text-sm">Recent Daily Avg Posts</p>
                    <p className="text-base sm:text-lg font-bold text-gray-800">
                      {Math.round(analysis.growth_analysis?.recent_daily_average_posts || 0)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Yearly Analysis */}
            {analysis.yearly_analysis && (
              <div className="bg-white rounded-2xl shadow-xl p-6">
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Calendar className="text-purple-600" size={28} />
                  Yearly Analysis
                </h2>
                
                {analysis.yearly_analysis.overall_trends && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">Overall Trends</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-gray-600 text-sm">Total Messages</p>
                        <p className="text-xl font-bold text-blue-600">
                          {analysis.yearly_analysis.overall_trends.total_period_messages?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-gray-600 text-sm">Total Views</p>
                        <p className="text-xl font-bold text-green-600">
                          {analysis.yearly_analysis.overall_trends.total_period_views?.toLocaleString() || 0}
                        </p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-gray-600 text-sm">Message Growth</p>
                        <p className="text-xl font-bold text-purple-600">
                          {analysis.yearly_analysis.overall_trends.overall_message_growth || 0}%
                        </p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-4">
                        <p className="text-gray-600 text-sm">View Growth</p>
                        <p className="text-xl font-bold text-orange-600">
                          {analysis.yearly_analysis.overall_trends.overall_view_growth || 0}%
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {analysis.yearly_analysis.yearly_breakdown && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Yearly Breakdown</h3>
                    <div className="space-y-6">
                      {Object.entries(analysis.yearly_analysis.yearly_breakdown).map(([year, data]) => (
                        <div key={year} className="border border-gray-200 rounded-lg p-4">
                          <h4 className="font-semibold text-lg mb-3">{year}</h4>
                          
                          {/* Main Stats */}
                          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                            <div className="bg-gray-50 rounded p-3">
                              <p className="text-sm text-gray-600">Total Messages</p>
                              <p className="font-bold">{data.total_messages?.toLocaleString() || 0}</p>
                            </div>
                            <div className="bg-gray-50 rounded p-3">
                              <p className="text-sm text-gray-600">Total Views</p>
                              <p className="font-bold">{data.total_views?.toLocaleString() || 0}</p>
                            </div>
                            <div className="bg-gray-50 rounded p-3">
                              <p className="text-sm text-gray-600">Engagement Rate</p>
                              <p className="font-bold">{data.engagement_rate || 0}%</p>
                            </div>
                            <div className="bg-gray-50 rounded p-3">
                              <p className="text-sm text-gray-600">Media %</p>
                              <p className="font-bold">{data.media_content_percentage || 0}%</p>
                            </div>
                          </div>

                          {/* Additional Stats */}
                          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                            <div className="bg-blue-50 rounded p-3">
                              <p className="text-sm text-gray-600">Avg Views/Message</p>
                              <p className="font-bold">{Math.round(data.average_views_per_message || 0).toLocaleString()}</p>
                            </div>
                            <div className="bg-green-50 rounded p-3">
                              <p className="text-sm text-gray-600">Avg Forwards/Message</p>
                              <p className="font-bold">{data.average_forwards_per_message || 0}</p>
                            </div>
                            <div className="bg-purple-50 rounded p-3">
                              <p className="text-sm text-gray-600">Forward Rate</p>
                              <p className="font-bold">{data.forward_rate || 0}%</p>
                            </div>
                            <div className="bg-orange-50 rounded p-3">
                              <p className="text-sm text-gray-600">Peak Month</p>
                              <p className="font-bold">{data.peak_month ? `Month ${data.peak_month}` : 'N/A'}</p>
                            </div>
                          </div>

                          {/* Growth Metrics */}
                          {data.growth_metrics && Object.keys(data.growth_metrics).length > 0 && (
                            <div className="mb-4">
                              <h5 className="font-semibold mb-2">Growth Metrics</h5>
                              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {data.growth_metrics.message_growth_percent !== undefined && (
                                  <div className="bg-yellow-50 rounded p-3">
                                    <p className="text-sm text-gray-600">Message Growth</p>
                                    <p className="font-bold">{data.growth_metrics.message_growth_percent || 0}%</p>
                                  </div>
                                )}
                                {data.growth_metrics.view_growth_percent !== undefined && (
                                  <div className="bg-pink-50 rounded p-3">
                                    <p className="text-sm text-gray-600">View Growth</p>
                                    <p className="font-bold">{data.growth_metrics.view_growth_percent || 0}%</p>
                                  </div>
                                )}
                                <div className="bg-indigo-50 rounded p-3">
                                  <p className="text-sm text-gray-600">Most Active Month</p>
                                  <p className="font-bold capitalize">{data.most_active_month || 'N/A'}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Seasonal Analysis */}
                          {data.seasonal_analysis && data.seasonal_analysis.seasonal_distribution && (
                            <div>
                              <h5 className="font-semibold mb-2">Seasonal Distribution (Avg Views)</h5>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {Object.entries(data.seasonal_analysis.seasonal_distribution).map(([season, avg_views]) => (
                                  <div key={season} className="bg-gray-50 rounded p-3 text-center">
                                    <p className="text-sm text-gray-600 capitalize">{season}</p>
                                    <p className="font-bold">{Math.round(avg_views || 0).toLocaleString()}</p>
                                    <p className="text-xs text-gray-500">avg views</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Monthly Average Views */}
                          {data.monthly_average_views && (
                            <div className="mt-4">
                              <h5 className="font-semibold mb-2">Monthly Average Views</h5>
                              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                                {Object.entries(data.monthly_average_views).map(([month, avg_views]) => (
                                  <div key={month} className="bg-gray-50 rounded p-2 text-center">
                                    <p className="text-xs text-gray-600">
                                      {new Date(2000, month - 1).toLocaleString('default', { month: 'short' })}
                                    </p>
                                    <p className="font-bold text-sm">{Math.round(avg_views || 0).toLocaleString()}</p>
                                  </div>
                                ))}
                              </div>
                              <div className="mt-2 flex justify-between text-sm">
                                <div className="text-gray-600">
                                  Best Month: <span className="font-semibold">{data.best_month_by_avg_views ? new Date(2000, data.best_month_by_avg_views - 1).toLocaleString('default', { month: 'long' }) : 'N/A'}</span>
                                </div>
                                <div className="text-gray-600">
                                  Peak: <span className="font-semibold">{Math.round(data.best_month_avg_views || 0).toLocaleString()} views</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Recent Messages */}
            {analysis.recent_messages && analysis.recent_messages.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6">
                <h2 className="text-lg sm:text-2xl font-bold mb-4 flex items-center gap-2">
                  <MessageCircle className="text-purple-600" size={window.innerWidth >= 640 ? 28 : 20} />
                  <span className="hidden sm:inline">Recent Messages (Last 20)</span>
                  <span className="sm:hidden">Recent Messages</span>
                </h2>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {analysis.recent_messages.map((message, index) => (
                    <div key={index} className="border-b border-gray-200 pb-3 last:border-0">
                      <div className="flex flex-col sm:flex-row justify-between items-start gap-2 mb-2">
                        <p className="text-gray-800 flex-1 text-sm sm:text-base">
                          {message.text || <em className="text-gray-500">Media or non-text message</em>}
                        </p>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {message.date ? new Date(message.date).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                        {message.views !== null && (
                          <span className="flex items-center gap-1">
                            <Eye size={window.innerWidth >= 640 ? 14 : 12} />
                            {message.views?.toLocaleString()}
                          </span>
                        )}
                        {message.forwards !== null && (
                          <span className="flex items-center gap-1">
                            <Share2 size={window.innerWidth >= 640 ? 14 : 12} />
                            {message.forwards?.toLocaleString()}
                          </span>
                        )}
                        {message.reactions > 0 && (
                          <span className="flex items-center gap-1">
                            <Heart size={window.innerWidth >= 640 ? 14 : 12} />
                            {message.reactions}
                          </span>
                        )}
                        {message.media_type && (
                          <span className="flex items-center gap-1">
                            <Zap size={window.innerWidth >= 640 ? 14 : 12} />
                            <span className="hidden sm:inline">{message.media_type}</span>
                            <span className="sm:hidden">{message.media_type.slice(0, 8)}...</span>
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
